import React, { useRef } from "react";
import { Box } from "@mui/material";

const SWIPE_THRESHOLD = 50;

/**
 * A lightweight replacement for react-swipeable-views.
 * Supports touch swipe, optional mouse drag, slide animation, and programmatic index control.
 * Props mirror the original library: index, onChangeIndex, enableMouseEvents, slideClassName.
 */
export default function SwipeViews({
  index = 0,
  onChangeIndex,
  enableMouseEvents,
  slideClassName,
  children,
  ...other
}) {
  const startXRef = useRef(null);
  const childArray = React.Children.toArray(children);
  const count = childArray.length;

  function onDragStart(clientX) {
    startXRef.current = clientX;
  }

  function onDragEnd(clientX) {
    if (startXRef.current === null) return;
    const delta = startXRef.current - clientX;
    startXRef.current = null;
    if (Math.abs(delta) >= SWIPE_THRESHOLD) {
      if (delta > 0 && index < count - 1) onChangeIndex?.(index + 1);
      else if (delta < 0 && index > 0) onChangeIndex?.(index - 1);
    }
  }

  function onDragCancel() {
    startXRef.current = null;
  }

  return (
    <Box sx={{ overflow: "hidden" }} {...other}>
      <Box
        sx={{
          display: "flex",
          transform: `translateX(-${index * 100}%)`,
          transition: "transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1)",
          willChange: "transform",
        }}
        onTouchStart={(e) => onDragStart(e.touches[0].clientX)}
        onTouchEnd={(e) => onDragEnd(e.changedTouches[0].clientX)}
        onMouseDown={enableMouseEvents ? (e) => onDragStart(e.clientX) : undefined}
        onMouseUp={enableMouseEvents ? (e) => onDragEnd(e.clientX) : undefined}
        onMouseLeave={enableMouseEvents ? onDragCancel : undefined}
      >
        {childArray.map((child, i) => (
          <Box key={i} className={slideClassName} sx={{ minWidth: "100%", flexShrink: 0 }}>
            {child}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
